import { Head, Link, useForm } from "@inertiajs/react"
import { BookOpen, Plus } from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

// The coach's Kingdom Factors books. With one book the controller opens it
// straight away, so this page is the first-book form or the list of several.
export default function BooksIndex({ currentUser, books = [], nextVolumes = {} }) {
  const form = useForm({ community: "", volume: 1 })
  const existing = Object.keys(nextVolumes).find(
    (community) => community.toLowerCase() === form.data.community.trim().toLowerCase()
  )
  const nextVolume = existing ? nextVolumes[existing] : null

  const submit = (event) => {
    event.preventDefault()
    form.transform((data) => ({ book: { community: data.community, volume: nextVolume || 1 } }))
    form.post("/app/coach/books")
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="My Book - Kingdom Factor" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">My Book</h1>
          <p className="text-zinc-600">
            Write a Kingdom Factors book. Each chapter is one Christian business leader in your community.
          </p>
        </div>

        {books.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            {books.map((book) => (
              <Link key={book.id} href={`/app/coach/books/${book.id}`}>
                <Card className="h-full transition-colors hover:border-kf-red">
                  <CardContent className="flex items-start gap-3">
                    <BookOpen className="mt-1 h-5 w-5 text-kf-red" />
                    <div>
                      <div className="font-semibold text-zinc-900">{book.title}</div>
                      <div className="text-sm text-zinc-600">
                        {book.approved} of {book.chapters} chapters approved
                        {book.sentToPrint && " · Sent to print"}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle className="text-lg">{books.length ? "Start a new book" : "Start your book"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="community">Your community</Label>
                <div className="flex items-center gap-2">
                  <span className="whitespace-nowrap text-sm text-zinc-600">Kingdom Factors of</span>
                  <Input
                    id="community"
                    value={form.data.community}
                    onChange={(event) => form.setData("community", event.target.value)}
                    placeholder="Indy"
                    maxLength={60}
                    required
                  />
                </div>
                <p className="text-sm text-zinc-500">
                  The title is fixed by Kingdom Factor. Only the community is yours.
                </p>
                {nextVolume && (
                  <p className="text-sm text-zinc-700">
                    You already have a book for {existing}. This one becomes <b>Volume {nextVolume}</b>. For a
                    standalone book, use a different community.
                  </p>
                )}
                {(form.errors.base || form.errors.community) && (
                  <p className="text-sm text-red-600">{form.errors.base || form.errors.community}</p>
                )}
              </div>
              <Button type="submit" disabled={form.processing}>
                <Plus className="h-4 w-4" />
                {nextVolume ? `Start Volume ${nextVolume}` : "Start the book"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </CoachLayout>
  )
}
